import React, { useEffect, useRef, useState } from 'react';
import './Result.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CLASS_CFG = {
  0: { color:'#4dd0e1', bg:'rgba(77,208,225,.09)', border:'rgba(77,208,225,.28)', icon:'🌡️' },
  1: { color:'#fbbf24', bg:'rgba(251,191,36,.09)',  border:'rgba(251,191,36,.28)',  icon:'⚠️' },
  2: { color:'#a78bfa', bg:'rgba(167,139,250,.09)', border:'rgba(167,139,250,.28)', icon:'🧠' },
};

function Bar({ pct, color }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => { if (ref.current) ref.current.style.width = `${pct}%`; }, 120);
    return () => clearTimeout(t);
  }, [pct]);
  return <div className="bar-bg"><div className="bar-fill" ref={ref} style={{ background:color }}/></div>;
}

function ConfRing({ pct, color }) {
  const R = 50, C = 2 * Math.PI * R;
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current) ref.current.style.strokeDashoffset = `${C*(1-pct/100)}`;
    }, 160);
    return () => clearTimeout(t);
  }, [pct, C]);
  return (
    <div className="conf-ring">
      <svg viewBox="0 0 120 120" width="116" height="116">
        <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="10"/>
        <circle ref={ref} cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C}
          transform="rotate(-90 60 60)"
          style={{ transition:'stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)' }}/>
      </svg>
      <div className="conf-inner">
        <span className="conf-pct">{pct}%</span>
        <span className="conf-word" style={{ color }}>
          {pct>=90?'Sangat Tinggi':pct>=70?'Tinggi':pct>=50?'Sedang':'Rendah'}
        </span>
      </div>
    </div>
  );
}

export default function Result({ result, go }) {
  const [pdfLoading, setPdfLoad] = useState(false);
  const [pdfError, setPdfError]  = useState('');

  if (!result) {
    return (
      <div className="res-empty">
        <p>Tidak ada hasil — lakukan diagnosis terlebih dahulu.</p>
        <button className="btn-back" onClick={() => go('diagnosis')}>← Kembali</button>
      </div>
    );
  }

  const { prediction, specific_diagnosis, class_probabilities, model_metrics, input_vals } = result;
  const cfg = CLASS_CFG[prediction.label_id] ?? CLASS_CFG[0];

  const handleDownloadPDF = async () => {
    setPdfError(''); setPdfLoad(true);
    try {
      // Rebuild input from saved vals
      const input = {};
      if (input_vals) {
        Object.entries(input_vals).forEach(([k,v]) => { input[k] = parseFloat(v)||0; });
      }
      const res = await fetch(`${API}/api/predict/pdf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `neurascan_hasil_${Date.now()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError(`Gagal unduh PDF: ${e.message}`);
    } finally { setPdfLoad(false); }
  };

  return (
    <div className="res-page iso-grid-bg">
      {/* Dekorasi latar */}
      <div className="page-decor" aria-hidden="true">
        <span className="pd-blob pd-1"/>
        <span className="pd-blob pd-2"/>
        <span className="pd-ring pd-r1"/>
        <span className="pd-glow"/>
      </div>
      <div className="res-container">

        <button className="btn-back" onClick={() => go('diagnosis')}>← Ubah Data</button>

        {/* Hero */}
        <div className="res-hero" style={{'--rc':cfg.color,'--rbg':cfg.bg,'--rbd':cfg.border}}>
          <div className="rh-left">
            <div className="rh-icon-wrap">
              <div className="rh-pulse"/>
              <span className="rh-icon">{cfg.icon}</span>
            </div>
            <div className="rh-text">
              <div className="rh-meta">Hasil Klasifikasi ML — 100 Fitur Klinis</div>
              <h1 className="rh-label">{prediction.label}</h1>
              <div className="rh-en">{prediction.label_en}</div>
            </div>
          </div>
          <ConfRing pct={prediction.confidence} color={cfg.color}/>
        </div>

        {/* PDF Download banner */}
        <div className="pdf-banner">
          <div className="pdf-info">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4dd0e1" strokeWidth="2">
              <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
            <div>
              <div className="pdf-title">Unduh Laporan PDF</div>
              <div className="pdf-sub">Laporan lengkap hasil diagnosis, rekomendasi klinis, dan data input dalam format PDF</div>
            </div>
          </div>
          <div className="pdf-btn-wrap">
            <button
              className={`btn-pdf-dl ${pdfLoading ? 'loading' : ''}`}
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
            >
              {pdfLoading
                ? <><span className="spin-dark"/>Membuat PDF...</>
                : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>Unduh PDF</>
              }
            </button>
          </div>
          {pdfError && <div className="pdf-error">{pdfError}</div>}
        </div>

        <div className="res-grid">
          {/* Left */}
          <div className="res-left">

            {/* Diagnosis card */}
            <div className="rcard">
              <div className="rcard-head">
                <span className="rcard-icon">🔬</span>
                <div>
                  <div className="rcard-title">Diagnosis Spesifik</div>
                  <div className="rcard-sub">Berdasarkan pola 100 fitur klinis</div>
                </div>
              </div>
              <div className="dx-name" style={{ color:cfg.color }}>{specific_diagnosis.name_id}</div>
              <div className="dx-en">{specific_diagnosis.name_en}</div>
              <p className="dx-desc">{specific_diagnosis.description}</p>

              <div className="rec-box">
                <div className="rec-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.2"><path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>
                  Rekomendasi Tindakan
                </div>
                <ol className="rec-list">
                  {specific_diagnosis.recommendations.map((r, i) => (
                    <li key={i}><span className="rec-n">{i+1}</span><span>{r}</span></li>
                  ))}
                </ol>
              </div>
            </div>

          </div>

          {/* Right */}
          <div className="res-right">

            {/* Probability */}
            <div className="rcard">
              <div className="rcard-head">
                <span className="rcard-icon">📊</span>
                <div>
                  <div className="rcard-title">Distribusi Probabilitas</div>
                  <div className="rcard-sub">Confidence tiap kategori kejang</div>
                </div>
              </div>
              <div className="prob-list">
                {[...class_probabilities].sort((a,b)=>b.probability-a.probability).map(cp => {
                  const c = CLASS_CFG[cp.label_id];
                  return (
                    <div key={cp.label_id} className={`prob-item${cp.label_id===prediction.label_id?' win':''}`}>
                      <div className="prob-row">
                        <span>{c.icon}</span>
                        <span className="prob-lbl">{cp.label}</span>
                        <span className="prob-pct" style={{ color:c.color }}>{cp.probability}%</span>
                      </div>
                      <Bar pct={cp.probability} color={c.color}/>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Model metrics */}
            {model_metrics && (
              <div className="rcard">
                <div className="rcard-head">
                  <span className="rcard-icon">🤖</span>
                  <div>
                    <div className="rcard-title">Performa Model</div>
                    <div className="rcard-sub">Voting Ensemble RF + GBM · 100 Fitur · 1200 Data</div>
                  </div>
                </div>
                <div className="metric-grid">
                  {[
                    { k:'accuracy', l:'Akurasi',  c:'#4dd0e1' },
                    { k:'f1_score', l:'F1 Score', c:'#4ade80' },
                    { k:'precision',l:'Presisi',  c:'#a78bfa' },
                    { k:'recall',   l:'Recall',   c:'#fbbf24' },
                  ].map(m => (
                    <div key={m.k} className="metric-box">
                      <div className="metric-v" style={{ color:m.c }}>{((model_metrics[m.k]||0)*100).toFixed(1)}%</div>
                      <div className="metric-l">{m.l}</div>
                    </div>
                  ))}
                </div>
                {model_metrics.cv_f1_mean && (
                  <div className="cv-row">
                    CV F1 (5-fold): <strong>{(model_metrics.cv_f1_mean*100).toFixed(1)}%</strong>
                    &nbsp;± {(model_metrics.cv_f1_std*100||0).toFixed(1)}%
                  </div>
                )}
              </div>
            )}

            {/* Jurnal */}
            <div className="rcard ref-compact">
              <div className="rcard-head">
                <span className="rcard-icon">📚</span>
                <div><div className="rcard-title">Referensi Jurnal Dataset</div></div>
              </div>
              {[
                'Steering Committee on Quality Improvement. (2011). Febrile Seizures Guideline. Pediatrics, 127(2). DOI:10.1542/peds.2010-3318',
                'Natsume J. et al. (2017). Clinical Features of Febrile Seizures. Brain Dev, 39(1). DOI:10.1016/j.braindev.2016.04.006',
                'Berg AT. et al. (1999). Predictors of Febrile Seizure Recurrence. NEJM, 341(12). DOI:10.1056/NEJM199909163411201',
              ].map((r,i) => <div key={i} className="ref-line">[{i+1}] {r}</div>)}
            </div>

          </div>
        </div>

        <div className="res-disclaimer">
          <strong>⚠️ Penting:</strong> Hasil ini adalah output model machine learning untuk skrining awal dan penelitian akademik.
          <strong> Bukan diagnosis medis klinis.</strong> Konsultasikan kondisi anak kepada dokter spesialis anak atau neurolog.
        </div>

        <div className="res-actions">
          <button className="btn-back-sm" onClick={() => go('diagnosis')}>← Diagnosis Ulang</button>
          <button className="btn-back-sm" onClick={() => go('about')}>Tentang Model</button>
          <button className="btn-back-sm" onClick={() => go('home')}>Beranda</button>
        </div>

      </div>
    </div>
  );
}
